import React, {useCallback, useMemo} from "react";
import type {Feature, FeatureCollection} from "geojson";

import {InteractiveGeoJsonLayer} from "@pg-mono/open-street-map";
import {RegionType} from "@pg-rp/common-types";

import {IInteractiveRegion} from "../types/IInteractiveRegion";

interface IFeatureProperties {
    id: number;
    name: string;
    type?: RegionType;
    isStatic?: boolean;
}

interface IProps {
    regionsToHover?: number[];
    staticRegions: IInteractiveRegion[];
    regions: IInteractiveRegion[];
    hasAllDistrictBeenSelected?: boolean;
    selectedFeaturesState?: Parameters<typeof InteractiveGeoJsonLayer>[0]["selectedFeaturesState"];
    onRegionClick?: (regionId: number) => void;
}

export const InteractiveRegionsMapLayer = (props: IProps) => {
    const hasAnyDistrict = useMemo(() => props.regions.some((region) => region.type === RegionType.DISTRICT), [props.regions]);

    const getFeatureLabel = useCallback((feature: Feature, isSelected: boolean, isHovered: boolean) => {
        if (isSelected || isHovered) {
            return feature.properties?.name || "";
        }
    }, []);

    const shouldExtendBounds = useCallback(
        (feature: Feature) => {
            // If regions contains any districts, adjust map viewport to them, otherwise add all regions to extend bounds logic.
            return hasAnyDistrict ? feature.properties?.type === RegionType.DISTRICT : true;
        },
        [hasAnyDistrict]
    );

    const getFeatureId = useCallback((feature: Feature) => feature.properties?.id, []);

    const getStyle = useCallback(
        (feature: Feature, isSelected: boolean) => {
            const properties = feature.properties as IFeatureProperties;
            const forceHover = props.regionsToHover?.includes(properties.id);
            const forceSelection = props.hasAllDistrictBeenSelected && properties.type === RegionType.DISTRICT;

            return {
                color: getBorderColor(isSelected, properties),
                weight: getBorderWeight(isSelected, properties),
                opacity: 1,
                fillOpacity: getFillOpacity(forceSelection || isSelected, properties),
                fillColor: getFillColor(forceSelection || isSelected, properties),
                ...(forceHover ? getHighlightStyle() : {})
            };
        },
        [props.hasAllDistrictBeenSelected, props.regionsToHover]
    );

    const getHighlightStyle = useCallback(() => {
        return {
            fillOpacity: 0.4,
            color: getHighlightedBorderColor(),
            fillColor: getHighlightedFillColor()
        };
    }, []);

    const onFeatureClick = useCallback(
        (feature: Feature) => {
            const regionId = (feature.properties as {id: number}).id;
            props.onRegionClick?.(regionId);
        },
        [props.onRegionClick]
    );

    const shouldBringToFront = useCallback((feature: Feature) => {
        const properties = feature.properties as IFeatureProperties;
        // Don't bring SURROUNDINGS to front - they should stay in the background
        return properties.type !== RegionType.SURROUNDINGS;
    }, []);

    const regionGeoJsonData = useMemo(() => {
        return {
            type: "FeatureCollection",
            features: [
                ...props.staticRegions.map((region) => {
                    return {
                        type: "Feature",
                        properties: {
                            id: region.id,
                            name: region.short_name.split(",").pop(),
                            type: region.type,
                            isStatic: true
                        },
                        geometry:
                            region.area &&
                            ({
                                type: region.area.geo_area.type,
                                coordinates: region.area.geo_area.coordinates
                            } as Feature["geometry"])
                    };
                }),
                ...props.regions
                    .filter((region) => region.type !== RegionType.SURROUNDINGS)
                    .map((region) => {
                        return {
                            type: "Feature",
                            properties: {
                                id: region.id,
                                name: region.short_name.split(",").pop(),
                                type: region.type,
                                isStatic: false
                            },
                            geometry:
                                region.area &&
                                ({
                                    type: region.area?.geo_area.type,
                                    coordinates: region.area?.geo_area.coordinates
                                } as Feature["geometry"])
                        };
                    })
            ]
        } as FeatureCollection;
    }, [props.regions]);

    const surroundingGeoJsonData = useMemo(() => {
        return {
            type: "FeatureCollection",
            features: props.regions
                .filter((region) => region.type === RegionType.SURROUNDINGS)
                .map((region) => {
                    return {
                        type: "Feature",
                        properties: {
                            id: region.id,
                            name: region.short_name.split(",").pop(),
                            type: region.type,
                            isStatic: false
                        },
                        geometry:
                            region.area &&
                            ({
                                type: region.area?.geo_area.type,
                                coordinates: region.area?.geo_area.coordinates
                            } as Feature["geometry"])
                    };
                })
        } as FeatureCollection;
    }, [props.regions]);

    return (
        <>
            <InteractiveGeoJsonLayer
                selectedFeaturesState={props.selectedFeaturesState}
                getFeatureId={getFeatureId}
                getFeatureLabel={getFeatureLabel}
                getStyle={getStyle}
                shouldExtendBounds={shouldExtendBounds}
                getHighlightStyle={getHighlightStyle}
                onFeatureClick={onFeatureClick}
                shouldBringToFront={shouldBringToFront}
                data={surroundingGeoJsonData}
            />
            <InteractiveGeoJsonLayer
                selectedFeaturesState={props.selectedFeaturesState}
                getFeatureId={getFeatureId}
                getFeatureLabel={getFeatureLabel}
                getStyle={getStyle}
                shouldExtendBounds={shouldExtendBounds}
                getHighlightStyle={getHighlightStyle}
                onFeatureClick={onFeatureClick}
                shouldBringToFront={shouldBringToFront}
                data={regionGeoJsonData}
            />
        </>
    );
};

const getBorderColor = (isSelected: boolean, properties: IFeatureProperties | undefined) => {
    const {type: regionType} = properties || {};

    if (isSelected || regionType === RegionType.DISTRICT) {
        return "#fff";
    }

    return "#fff";
};

const getBorderWeight = (isSelected: boolean, properties: IFeatureProperties | undefined) => {
    const {type: regionType} = properties || {};

    if (regionType === RegionType.DISTRICT) {
        return 3;
    }

    return 1;
};

const getHighlightedBorderColor = () => {
    return "#fff";
};

const getHighlightedFillColor = () => {
    return "#EBFF00D9";
};

const getFillColor = (isSelected: boolean, properties: IFeatureProperties | undefined) => {
    if (properties?.isStatic) {
        return "#fff";
    }

    if (isSelected) {
        return "#EBFF00D9";
    }

    return "#DCDCDD";
};

const getFillOpacity = (isSelected: boolean, properties: IFeatureProperties | undefined) => {
    const {type: regionType} = properties || {};

    if (regionType === RegionType.CITY) {
        return 0.2;
    }

    if (isSelected) {
        return regionType === RegionType.DISTRICT ? 0.85 : 0.85;
    }

    if (regionType === RegionType.DISTRICT) {
        return 0.75;
    }

    return 0.75;
};
